import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, RotateCcw, Sparkles } from 'lucide-react'
import { runBotSandbox } from '../../api/botSandbox'
import useBotConfigStore from '../../store/useBotConfigStore'

// `enabled` and `publishedOnly` are for pages with no bot config draft of their own (the FAQ
// page): the server's capabilities decide whether to show it, and it tests what customers get.
export default function BotSandboxPanel({ tenantId, variant = 'dock', enabled, publishedOnly = false, title, hint, fill = false }) {
  const { t } = useTranslation()
  const capabilities = useBotConfigStore((state) => state.capabilities)
  const sandboxState = useBotConfigStore((state) => state.sandboxState)
  const sandboxSlots = useBotConfigStore((state) => state.sandboxSlots)
  const setSandboxState = useBotConfigStore((state) => state.setSandboxState)
  const saveDraft = useBotConfigStore((state) => state.saveDraft)
  const dirty = useBotConfigStore((state) => state.dirty)
  const hasUnpublishedChanges = useBotConfigStore((state) => state.hasUnpublishedChanges)
  const [text, setText] = useState('')
  const [history, setHistory] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [source, setSource] = useState(null)
  const listRef = useRef(null)

  // Keep the newest turn in view; the list scrolls inside the panel, never the page.
  useEffect(() => {
    const list = listRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [history, busy, error])

  if (!(enabled ?? capabilities?.sandbox)) return null

  const hasDraft = !publishedOnly && (dirty || hasUnpublishedChanges)
  const config = hasDraft ? (source ?? 'draft') : 'published'

  async function send() {
    const message = text.trim()
    if (!message || busy) return
    setBusy(true)
    setError(null)
    const priorHistory = history
    setHistory((items) => [...items, { role: 'user', content: message, text: message }])
    setText('')
    try {
      // Unsaved edits are never sent: persist them first so the test reflects what is on screen.
      if (dirty && config === 'draft') await saveDraft()
      const response = await runBotSandbox(tenantId, {
        message,
        history: priorHistory.slice(-20).map(({ role, content }) => ({ role, content })),
        slots: sandboxSlots,
        state: sandboxState,
        config,
      })
      const reply = response?.reply ?? ''
      setHistory((items) => [...items, { role: 'assistant', content: reply, text: reply }])
      setSandboxState(response?.state ?? null, response?.slots ?? {})
    } catch (requestError) {
      // Drop the unanswered user turn so it is not replayed in the next request.
      setHistory(priorHistory)
      setText(message)
      setError(requestError)
    } finally {
      setBusy(false)
    }
  }

  const draftLabel = dirty && config === 'draft' ? t('playbook.saveAndTry') : null

  return (
    <aside className={`${variant === 'drawer' ? 'fixed inset-y-0 right-0 z-40 w-full max-w-md' : 'w-full'} ${fill ? 'min-h-0 flex-1' : ''} flex flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-sm`} aria-label={title ?? t('playbook.sandbox')}>
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-text-main">
          <Sparkles size={16} className="shrink-0 text-primary" aria-hidden="true" />
          <span className="truncate">{title ?? t('playbook.sandbox')}</span>
        </h2>
        <div className="flex shrink-0 items-center gap-2">
          {!publishedOnly && <select aria-label={t('playbook.sandboxSource')} value={config} onChange={(event) => setSource(event.target.value)} className="rounded-lg border border-border bg-white px-2 py-1 text-xs text-text-muted">
            <option value="draft" disabled={!hasDraft}>{t('playbook.draft')}</option>
            <option value="published">{t('playbook.published')}</option>
          </select>}
          <button type="button" className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-text-muted hover:bg-primary-soft hover:text-primary" onClick={() => { setHistory([]); setError(null); setSandboxState(null, {}) }}>
            <RotateCcw size={12} aria-hidden="true" />{t('playbook.refresh')}
          </button>
        </div>
      </div>
      <div ref={listRef} className={`flex ${fill ? 'min-h-0' : 'min-h-44'} flex-1 flex-col gap-3 overflow-y-auto overscroll-contain p-4 text-sm`}>
        {history.length === 0 && <p className="text-text-secondary">{hint ?? t('playbook.sandboxHint')}</p>}
        {history.map((message, index) => (
          <div key={`${message.role}-${index}`} className={`${message.role === 'user' ? 'max-w-[85%] self-end rounded-2xl bg-primary-light px-3.5 py-2' : 'self-start'} whitespace-pre-wrap break-words leading-relaxed text-text-main`}>
            {message.text}
          </div>
        ))}
        {busy && <div className="flex gap-1 self-start py-1" aria-live="polite" aria-label="…">{[0, 1, 2].map((dot) => <span key={dot} className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60" style={{ animationDelay: `${dot * 150}ms` }} />)}</div>}
        {error && <p role="alert" className="text-xs text-red-600">{error?.response?.status === 503 ? t('playbook.sandboxUnavailable') : error?.response?.status === 429 ? t('playbook.sandboxRateLimited') : t('common.requestFailed')}</p>}
      </div>
      <div className="shrink-0 border-t border-border p-3">
        <div className="flex items-center gap-2 rounded-2xl border border-border bg-white py-1.5 pl-3 pr-1.5 focus-within:border-primary">
          <input className="min-w-0 flex-1 bg-transparent text-sm text-text-main outline-none placeholder:text-text-secondary" value={text} onChange={(event) => setText(event.target.value)} placeholder={t('playbook.sandboxInput')} onKeyDown={(event) => { if (event.key === 'Enter') send() }} />
          <button type="button" aria-label={draftLabel ?? t('playbook.send')} className={`flex h-8 shrink-0 items-center justify-center rounded-full bg-primary text-white hover:bg-primary-dark disabled:bg-border disabled:text-text-secondary ${draftLabel ? 'px-3 text-xs font-semibold' : 'w-8'}`} disabled={!text.trim() || busy} onClick={send}>
            {draftLabel ?? <ArrowUp size={16} aria-hidden="true" />}
          </button>
        </div>
      </div>
    </aside>
  )
}
